import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import SubscribeForm from "@/components/feature/SubscribeForm";
import usePageMeta from "@/hooks/usePageMeta";
import { contentPrinciples, siteInfo, siteStats, testimonials } from "@/mocks/news";

const workflow = [
  { step: "01", title: "收集", desc: "把公开渠道出现的说法、动作与公告统一登记，保留原始出现时间。" },
  { step: "02", title: "交叉验证", desc: "至少寻找一个独立于原始来源的第三方依据，找不到就标为线索。" },
  { step: "03", title: "归类", desc: "按人物动态、事件追踪、时间线梳理、快报专栏、行业观察五个栏目归位。" },
  { step: "04", title: "发布与更正", desc: "发布后如出现新依据，追加更正说明而不是静默修改或删除原文。" },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于料闻社",
        url: "https://sl858.cn/about",
        inLanguage: "zh-CN",
        description: siteInfo.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系料闻社",
        url: "https://sl858.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        url: "https://sl858.cn/",
        email: siteInfo.email,
        description: siteInfo.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sl858.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://sl858.cn/about" },
        ],
      },
    ],
    [],
  );

  usePageMeta({
    title: "关于我们 - 料闻社内容原则与联系方式｜独家黑料速递",
    description:
      "了解料闻社的品牌定位、内容原则与工作流程：如何收集速递条目、如何交叉验证、如何做更正，以及独家黑料速递的联系方式。",
    keywords: "关于料闻社,内容原则,独家黑料速递,更正说明,联系方式",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[1.3fr_1fr] lg:gap-14">
            <div className="w-full">
              <h1 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                关于料闻社
                <span className="mt-2 block text-base font-bold text-foreground-500 md:text-lg">
                  速递不是抢快，是把依据摆清楚
                </span>
              </h1>
              <div className="mt-5 space-y-3.5 text-sm leading-relaxed text-foreground-700">
                <p>{siteInfo.description}</p>
                <p>
                  我们不追求第一时间发出最刺激的说法，而是尽量把一条信息放回它的时间位置：它什么时候出现、
                  谁在什么时间做了对应的动作、哪一部分目前还无法验证。读者拿到的不只是一条结论，而是一段可以自己判断的过程。
                </p>
              </div>
              <Link
                to="/#feed"
                className="mt-7 inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-compass-3-line" />
                浏览今日速递
              </Link>
            </div>

            <div className="w-full rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">站点速览</h2>
              <dl className="mt-4 grid grid-cols-2 gap-x-5 gap-y-5">
                {siteStats.map((stat) => (
                  <div key={stat.label}>
                    <dt className="text-xs text-foreground-500">{stat.label}</dt>
                    <dd className="mt-1 flex items-baseline gap-1">
                      <span className="label-font text-xl font-semibold text-primary-600 md:text-2xl">
                        {stat.value}
                      </span>
                      <span className="text-xs text-foreground-500">{stat.unit}</span>
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 border-t border-background-200 pt-4 text-xs leading-relaxed text-foreground-500">
                站内人物与事件均为虚构设定，用于演示资讯站的信息组织方式与时间线梳理方法，不指向任何真实的个人或机构。
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <Reveal>
              <div className="max-w-2xl">
                <span className="label-font text-xs uppercase tracking-[0.24em] text-primary-600">
                  Principles
                </span>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  内容原则
                </h2>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                  这四条不是口号，而是决定一条信息能不能出现在站内的判断标准。
                </p>
              </div>
            </Reveal>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {contentPrinciples.map((item, index) => (
                <Reveal key={item.title} delay={index * 60}>
                  <div className="h-full rounded-lg border border-background-200 bg-background-100/70 p-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <Reveal>
              <div className="max-w-2xl">
                <span className="label-font text-xs uppercase tracking-[0.24em] text-primary-600">
                  Workflow
                </span>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  一条速递是怎么做出来的
                </h2>
              </div>
            </Reveal>

            <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {workflow.map((item, index) => (
                <li key={item.step} className="h-full">
                  <Reveal delay={index * 60} className="h-full">
                    <div className="relative h-full rounded-lg border border-background-200 bg-background-50 p-5">
                      <span className="label-font text-2xl font-bold text-background-400">
                        {item.step}
                      </span>
                      <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-[1400px] px-4 md:px-8">
            <Reveal>
              <div className="max-w-2xl">
                <span className="label-font text-xs uppercase tracking-[0.24em] text-primary-600">
                  Voices
                </span>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  用户口碑
                </h2>
              </div>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {testimonials.map((item, index) => (
                <Reveal key={item.name} delay={index * 60}>
                  <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100/70 p-5">
                    <div className="flex items-center gap-1 text-accent-500">
                      {[0, 1, 2, 3, 4].map((star) => (
                        <i key={star} className="ri-star-fill text-xs" />
                      ))}
                    </div>
                    <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                      「{item.text}」
                    </blockquote>
                    <figcaption className="mt-4 flex items-center gap-2 border-t border-background-200 pt-3 text-xs text-foreground-500">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-500 text-[11px] font-semibold text-background-50">
                        {item.name.slice(0, 1)}
                      </span>
                      <span className="font-medium text-foreground-800">{item.name}</span>
                      <span>·</span>
                      <span>{item.since}</span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 md:px-8 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
            <Reveal className="w-full">
              <div className="w-full">
                <span className="label-font text-xs uppercase tracking-[0.24em] text-primary-600">
                  Contact
                </span>
                <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  联系方式
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  内容纠错、线索投递与合作咨询都可以通过同一个入口提交，我们按提交类型分流处理。
                </p>

                <ul className="mt-7 space-y-4">
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line" />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">邮箱</span>
                      <span className="mt-0.5 block text-sm text-foreground-600">
                        {siteInfo.email}
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-time-line" />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">回复时间</span>
                      <span className="mt-0.5 block text-sm text-foreground-600">
                        {siteInfo.replyTime}
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-refresh-line" />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">更新节奏</span>
                      <span className="mt-0.5 block text-sm text-foreground-600">
                        {siteInfo.updateNote}
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal className="w-full" delay={80}>
              <SubscribeForm />
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}